import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ChatItem } from "@/types";
import {
  appendEventToChatItems,
  appendTextToChatItems,
} from "@/utils/toolChevrons";

/**
 * Represents the status of the current execution.
 *
 * IDLE           — No active execution, resting state before a prompt or after reset.
 * SENDING        — WebSocket ping sent with prompt, waiting for the backend to receive it.
 * QUEUED         — Backend received the request and enqueued it in SQS, but not picked up yet.
 * IN_PROGRESS    — Orchestrator Lambda/AgentCore is processing the request.
 * COMPLETED      — Backend finished successfully and all data is sent back to the Frontend.
 * FAILED         — Something went wrong on the backend. Error details are in state.
 * DUPLICATE      — Backend rejected the request because one is already in progress for this project.
 */
export enum ExecutionStatus {
  IDLE = "IDLE",
  SENDING = "SENDING",
  QUEUED = "QUEUED",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  DUPLICATE = "DUPLICATE",
}

/**
 * Completion status sent by the backend in the "complete" WebSocket message.
 */
export enum CompletionStatus {
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

/**
 * Acknowledgement status sent by the backend in the "acknowledgement" WebSocket message.
 */
export enum AcknowledgementStatus {
  QUEUED = "QUEUED",
  DUPLICATE = "DUPLICATE",
}

/**
 * A progress step reported by the backend during agent processing.
 * Derived from ProgressMessage in AFXTACOWebsocketLambda (type: "progress").
 */
export interface ProgressStep {
  message: string;
  step?: number;
  totalSteps?: number;
  timestamp: string;
}

/**
 * An asset update notification from the backend.
 * Derived from AssetUpdateMessage in AFXTACOWebsocketLambda (type: "asset_update").
 */
export interface AssetUpdate {
  /**
   * Project-relative path of the changed asset (e.g. `summary/summary.md`,
   * `cost.md`) — the stable identifier the page maps to a tab for its
   * "new content" indicator.
   */
  path: string;
  /** Optional display hints from the agent. */
  assetType?: string;
  tabLabel: string;
  timestamp: string;
}

/**
 * A system event from the agent's agentic loop (tool calls, completions, cycle boundaries).
 * Streamed via WebSocket from AFXTACOAgent and displayed in the Thinking tab.
 */
export interface AgentSystemEvent {
  eventType: string; // "tool_start", "tool_complete", "cycle_start"
  toolName?: string;
  details?: string;
  timestamp: string;
}

/**
 * Tracks the lifecycle of a single user prompt submission — from the moment the
 * WebSocket ping is sent through to completion or failure. Updated by WebSocket
 * messages from the backend and consumed by UI components via useSelector.
 */
export interface ExecutionState {
  /** Current execution status */
  status: ExecutionStatus;
  /** Project ID of the current execution */
  projectId: string | null;
  /** Session ID of the current execution */
  sessionId: string | null;
  /** Client request ID of the current execution */
  clientRequestId: string | null;
  /** Accumulated streaming content from agent_response messages */
  streamingContent: string;
  /** Whether the current streaming content is a partial response */
  isStreamingPartial: boolean;
  /** Content segments that were completed before a system event boundary (e.g., tool_start, tool_complete). **/
  completedSegments: string[];
  /**
   * Ordered text + tool-call items for the in-flight assistant turn, used by
   * the `thinkingChevron` feature to render tool chevrons inline between text.
   *
   * Maintained alongside `streamingContent`/`completedSegments` (which the
   * flag-off path still uses) rather than replacing them, so both rendering
   * paths stay live and the feature is a pure flag flip.
   */
  streamingChatItems: ChatItem[];
  /** Accumulated streaming reasoning from reasoning_delta messages */
  streamingReasoning: string;
  /** Progress steps received from the backend */
  progressSteps: ProgressStep[];
  /** Asset updates received from the backend */
  assetUpdates: AssetUpdate[];
  /** System events from the agent's agentic loop (tool calls, completions) */
  systemEvents: AgentSystemEvent[];
  /** Error information if execution failed */
  errorCode: string | null;
  errorMessage: string | null;
  /** Final completion version (if any) */
  completedVersion: number | null;
  /** Whether the project is newly created for allowing thinking tab to open on first prompt*/
  isNewProjectFlow: boolean;
}

const initialState: ExecutionState = {
  status: ExecutionStatus.IDLE,
  projectId: null,
  sessionId: null,
  clientRequestId: null,
  streamingContent: "",
  isStreamingPartial: false,
  completedSegments: [],
  streamingChatItems: [],
  streamingReasoning: "",
  progressSteps: [],
  assetUpdates: [],
  systemEvents: [],
  errorCode: null,
  errorMessage: null,
  completedVersion: null,
  isNewProjectFlow: false,
};

const ExecutionSlice = createSlice({
  name: "Execution",
  initialState,
  reducers: {
    /**
     * Called when the frontend sends a WebSocket ping (starts a new execution).
     *
     * Defense-in-depth: if the execution is already in an active phase
     * (SENDING, QUEUED, or IN_PROGRESS), the dispatch is ignored to prevent
     * a concurrent submission from nuking the in-flight streaming state.
     * The primary guard lives in IDEChatPanel/HomePage, but this ensures
     * correctness even if a caller forgets.
     *
     * Thinking-tab continuity: when the new execution is for the same project
     * as the previous one, we PRESERVE `systemEvents` so the Thinking tab's
     * activity timeline continues accumulating across turns (new events are
     * appended and auto-scrolled into view). Previously we cleared the array
     * on every turn, which caused the tab to flash to "Waiting for agent
     * activity…" on each new prompt and lose the previous turn's events.
     * We only wipe the array when the user switches to a different project,
     * since those events belong to a different conversation context.
     */
    startExecution(
      state,
      action: PayloadAction<{
        projectId: string;
        sessionId: string;
        clientRequestId: string;
        /** True only on the HomePage new-project submit path. */
        isNewProject?: boolean;
      }>,
    ) {
      // Reject if an execution is already in flight
      if (isExecutionActive(state.status)) {
        return;
      }

      const isSameProject = state.projectId === action.payload.projectId;

      state.status = ExecutionStatus.SENDING;
      state.projectId = action.payload.projectId;
      state.sessionId = action.payload.sessionId;
      state.clientRequestId = action.payload.clientRequestId;
      state.streamingContent = "";
      state.isStreamingPartial = false;
      state.completedSegments = [];
      state.streamingChatItems = [];
      state.streamingReasoning = "";
      state.progressSteps = [];
      state.assetUpdates = [];
      // Preserve systemEvents within the same project so the Thinking tab
      // shows a continuous timeline across turns. Clear on project switch.
      if (!isSameProject) {
        state.systemEvents = [];
      }
      state.errorCode = null;
      state.errorMessage = null;
      state.completedVersion = null;
      // Default false so follow-up chats don't re-trigger new-project UX.
      state.isNewProjectFlow = action.payload.isNewProject === true;
    },

    /**
     * Called when an acknowledgement message is received from the backend.
     *
     * Only processes acknowledgements when the execution is in the SENDING
     * state (i.e., we just sent a ping and are waiting for the backend
     * to acknowledge it). This prevents a stale or delayed DUPLICATE
     * acknowledgement from corrupting an active IN_PROGRESS streaming session.
     */
    setAcknowledged(
      state,
      action: PayloadAction<{ status: AcknowledgementStatus }>,
    ) {
      // Only accept acknowledgements when we're in SENDING state.
      // If the execution has already progressed past SENDING (e.g., IN_PROGRESS
      // because text_delta arrived before the ack), ignore stale acks.
      if (state.status !== ExecutionStatus.SENDING) {
        return;
      }

      if (action.payload.status === AcknowledgementStatus.QUEUED) {
        state.status = ExecutionStatus.QUEUED;
      } else if (action.payload.status === AcknowledgementStatus.DUPLICATE) {
        state.status = ExecutionStatus.DUPLICATE;
      }
    },

    /**
     * Called when a progress message is received from the backend.
     */
    addProgress(state, action: PayloadAction<ProgressStep>) {
      state.status = ExecutionStatus.IN_PROGRESS;
      state.progressSteps.push(action.payload);
    },

    /**
     * Called when a reasoning_delta message is received (streaming agent thinking).
     */
    appendStreamingReasoning(state, action: PayloadAction<{ text: string }>) {
      state.status = ExecutionStatus.IN_PROGRESS;
      state.streamingReasoning += action.payload.text;
    },

    /**
     * Called when an agent_response message is received (streaming chat content).
     */
    appendStreamingContent(
      state,
      action: PayloadAction<{ content: string; isPartial: boolean }>,
    ) {
      state.status = ExecutionStatus.IN_PROGRESS;
      if (action.payload.isPartial) {
        state.streamingContent += action.payload.content;
        state.isStreamingPartial = true;
        // Mirror into the chevron timeline: merges with the trailing text item,
        // or starts a new one if tool activity intervened.
        appendTextToChatItems(state.streamingChatItems, action.payload.content);
      } else {
        // Final content — replace accumulated content with the complete response
        state.streamingContent = action.payload.content;
        state.isStreamingPartial = false;
      }
    },

    /**
     * Called when an asset_update message is received.
     */
    addAssetUpdate(state, action: PayloadAction<AssetUpdate>) {
      state.assetUpdates.push(action.payload);
    },

    /**
     * Called when a system_event message is received from the agent's agentic loop.
     * Populates the Thinking tab's activity timeline.
     */
    addSystemEvent(state, action: PayloadAction<AgentSystemEvent>) {
      state.status = ExecutionStatus.IN_PROGRESS;

      // Snapshot any accumulated streaming content as a completed segment.
      // Only push non-whitespace content to avoid empty bubbles, but always
      // reset the accumulator at the boundary so whitespace doesn't leak
      // into the next segment.
      const trimmed = state.streamingContent.trim();
      if (trimmed) {
        state.completedSegments.push(state.streamingContent);
      }
      // Always reset at the boundary
      state.streamingContent = "";
      state.isStreamingPartial = false;

      // Fold tool lifecycle events into the chevron timeline. Text already
      // landed via appendStreamingContent, so `agent_thought` is a no-op here
      // (see appendEventToChatItems) — that is what prevents the same narration
      // rendering both as a bubble and inside a chevron.
      appendEventToChatItems(state.streamingChatItems, action.payload);

      state.systemEvents.push(action.payload);
    },

    /**
     * Called when a complete message is received.
     */
    setCompleted(
      state,
      action: PayloadAction<{
        status: CompletionStatus;
        version?: number;
      }>,
    ) {
      state.status =
        action.payload.status === CompletionStatus.COMPLETED
          ? ExecutionStatus.COMPLETED
          : ExecutionStatus.FAILED;
      state.completedVersion = action.payload.version ?? null;
      state.isStreamingPartial = false;
    },

    /**
     * Called when an error message is received.
     */
    setError(
      state,
      action: PayloadAction<{ errorCode: string; errorMessage: string }>,
    ) {
      state.status = ExecutionStatus.FAILED;
      state.errorCode = action.payload.errorCode;
      state.errorMessage = action.payload.errorMessage;
      state.isStreamingPartial = false;
    },

    /**
     * Reset execution state back to idle.
     *
     * Preserves `systemEvents` and `projectId` so the Thinking tab can
     * continue displaying the activity timeline after completion. These
     * fields are cleared on the NEXT execution start (`startExecution`),
     * which is the natural lifecycle boundary.
     */
    resetExecution(state) {
      const preservedSystemEvents = state.systemEvents;
      const preservedProjectId = state.projectId;
      Object.assign(state, initialState);
      state.systemEvents = preservedSystemEvents;
      state.projectId = preservedProjectId;
    },
  },
});

export const {
  startExecution,
  setAcknowledged,
  addProgress,
  appendStreamingReasoning,
  appendStreamingContent,
  addAssetUpdate,
  addSystemEvent,
  setCompleted,
  setError,
  resetExecution,
} = ExecutionSlice.actions;

/**
 * Returns true if the execution is in any active/loading phase
 * (sending the WS ping, queued in SQS, or being processed by the orchestrator).
 */
export const isExecutionActive = (status: ExecutionStatus): boolean =>
  status === ExecutionStatus.SENDING ||
  status === ExecutionStatus.QUEUED ||
  status === ExecutionStatus.IN_PROGRESS;

export default ExecutionSlice.reducer;
